import { ErrorBoundary } from '@datadog/rum-react-integration';
import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import moment from 'moment';
import React, { FC, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { Provider, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { AppUpgradeConfirmation } from 'components/molecules/app-update-confirmation';
import { ToastDescription, toastService } from 'components/molecules/toast';
import { AccountNotFound } from 'components/organisms/account';
import { AppCrash } from 'components/organisms/app-crash';
import { TokenErrorStatus, TokenError } from 'components/organisms/token-error';
import { GlobalStyle } from 'components/styles/global';
import { theme } from 'components/styles/theme';
import { APP_CRASH_KEY, POTENTIAL_APP_CRASH_KEY, RoutePaths } from 'constant';
import { OTA_LS_KEY } from 'constant/constant';
import { CustomerPortalPreviewProvider } from 'contexts/providers';
import { CustomScroll, OsList } from 'customScroll';
import { setQueryClientAction } from 'data/core/actions';
import { useTranslate } from 'hooks/use-translation';
import { OzoneFonts } from 'ozone/atoms';
import { queryClient, useAccountMetaQuery } from 'queries';
import { MobileViewOverlay } from 'routes/mobile-view-overlay';
import { Routes } from 'routes/routes';
import { setOkta, getOkta, logger } from 'services';
import { localeData } from 'translations/en-moment';
import translations from 'translations/en.json';
import { isLocalhost } from 'utils/is-localhost';
import { isOrbitsEnvironment } from 'utils/is-nitro-env';
import { db } from 'workers';
import { PageLoading } from './components/atoms/loading';
import { CONFIG } from './config/okta';
import { store } from './data';

const VOID_FN = () => {};

const SetupOkta = React.memo(() => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  useEffect(() => {
    dispatch(setQueryClientAction(queryClient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, queryClient]);
  const { data, isLoading } = useAccountMetaQuery({
    onSuccess: ({ okta: { clientId, issuerDomain } }) => {
      const oktaConfig = CONFIG(issuerDomain, clientId);
      const authInstance = new OktaAuth(oktaConfig);

      setOkta(authInstance);
    },
    onError: (err) => {
      logger.error('Account meta failed', { error: err as Error });
    },
  });

  const oktaAuth = getOkta();
  const isAccountActionPage = pathname === RoutePaths.activate;

  return isLoading ? (
    <PageLoading />
  ) : oktaAuth && data ? (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={VOID_FN}>
      <Helmet
        titleTemplate={`%s - ${data.company?.companyName}`}
        defaultTitle={data.company?.companyName}
      >
        <link rel="icon" type="image/png" href={data.company?.companyLogoUrl} />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={data.company?.companyLogoUrl}
        />
      </Helmet>
      <Routes />
    </Security>
  ) : isAccountActionPage ? (
    <TokenError status={TokenErrorStatus.AlreadyCompleted} />
  ) : (
    <AccountNotFound />
  );
});

export const App: FC = () => {
  const locale = 'en';

  useLayoutEffect(() => {
    moment.updateLocale(locale, localeData);
  }, [locale]);

  const AppUpgradeConfirmationComponent: FC = () => (
    <QueryClientProvider client={queryClient}>
      <IntlProvider locale={locale} messages={translations}>
        <AppUpgradeConfirmation />
      </IntlProvider>
    </QueryClientProvider>
  );

  const OTAUpdatesComponent: FC = () => {
    const translate = useTranslate();

    useEffect(() => {
      if (window.BroadcastChannel && !isLocalhost() && !isOrbitsEnvironment()) {
        const broadcast = new BroadcastChannel('count-channel');

        broadcast.onmessage = (ev) => {
          const isShow = localStorage.getItem(OTA_LS_KEY);

          if (ev?.data?.type === 'new_ota_updates_available') {
            if (isShow !== 'true') {
              localStorage.setItem(OTA_LS_KEY, 'true');
              toastService({
                duration: 10 * 10 * 60 * 1000,
                message: (
                  <ToastDescription
                    buttonText={translate('ota.action')}
                    message={translate('ota.sub_title')}
                    onClick={() => {
                      localStorage.removeItem(OTA_LS_KEY);
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      window.location.reload(true);
                    }}
                  />
                ),
                title: translate('ota.title'),
              }).info();
            }
          }
        };
      }
    }, [translate]);

    return <></>;
  };

  const fallback = (error: Error) => {
    /**
     * Handling errors from indexDb here
     * VersionError occurs on an existing window when the indexDb version is downgraded.
     * This causes the existing tab to break its connection, and the app crashes.
     * Trying to mitigate this by reloading
     * DatabaseClosedError occurs on an existing window when a new window upgrades indexDB version
     */
    if (error?.message?.includes('VersionError')) {
      logger.error(`${POTENTIAL_APP_CRASH_KEY}: ${error?.message}`, {
        error,
      });
      db.delete();
      window.location.reload();

      return <></>;
    }

    // need to check if DatabaseClosedError occurs other than when upgrading
    if (error?.message?.includes('DatabaseClosedError')) {
      logger.error(`${POTENTIAL_APP_CRASH_KEY}: ${error?.message}`, {
        error,
      });

      return <></>;
    }

    logger.error(`${APP_CRASH_KEY}: ${error.message}`, {
      error,
    });

    return (
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <AppCrash />
        </Provider>
      </QueryClientProvider>
    );
  };

  return (
    <>
      <ErrorBoundary fallback={fallback}>
        <CustomerPortalPreviewProvider>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <BrowserRouter>
                <ThemeProvider theme={theme}>
                  <IntlProvider locale={locale} messages={translations}>
                    <MobileViewOverlay>
                      <CustomScroll blacklist={[OsList.Mac]} />
                      <OzoneFonts />
                      <SetupOkta />
                      <OTAUpdatesComponent />
                    </MobileViewOverlay>
                  </IntlProvider>
                  <GlobalStyle />
                </ThemeProvider>
              </BrowserRouter>
            </Provider>
          </QueryClientProvider>
        </CustomerPortalPreviewProvider>
      </ErrorBoundary>
      <AppUpgradeConfirmationComponent />
    </>
  );
};
